/* eslint-disable no-extra-boolean-cast */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
} from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import {
  Search as SearchIcon,
  XCircle as XCircleIcon,
  Edit as EditIcon,
  PlusSquare as PlusSquareIcon,
  Trash as TrashIcon,
} from 'react-feather';

import Grid from '@mui/material/Grid';

import {
  setReportQuery,
  setReportPage,
  setReportLimit,
} from '../../../actions/reportsList';

import useStyles from './styles';

import { deleteComparativeReport } from '../../../requests/api/reports';

function Results({
  className,
  reports,
  count,
  sort,
  setSort,
  sortOptions,
  cleanFilters,
  onRefresh,
  ...rest
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [currentReports, setCurrentReports] = useState(reports);

  const [openDelete, setOpenDelete] = React.useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const account = useSelector((state) => state.account);

  const {
    reportQuery,
    reportPage,
    reportLimit,
  } = useSelector((state) => state.reportsList);

  useEffect(() => { setCurrentReports(reports); }, [reports]);

  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const handleQueryChange = (event) => {
    event.persist();
    dispatch(setReportQuery(event.target.value));
    dispatch(setReportPage(0));
  };

  const handleSortChange = (event) => {
    event.persist();
    setSort(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    dispatch(setReportPage(newPage));
  };

  const handleLimitChange = (event) => {
    dispatch(setReportLimit(event.target.value));
  };

  const handleOpenDelete = (report) => {
    setReportToDelete(report);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setReportToDelete(null);
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;

    setLoadingDelete(true);
    try {
      const response = await deleteComparativeReport([reportToDelete.id]);
      if (response.success) {
        enqueueSnackbar('Reporte eliminado exitosamente.', {
          variant: 'success',
        });
        // Actualizar la lista local removiendo el reporte eliminado
        setCurrentReports(currentReports.filter((r) => r.id !== reportToDelete.id));
        // Refrescar la lista desde el servidor
        if (onRefresh) {
          onRefresh();
        }
      } else {
        enqueueSnackbar(response.message || 'Error al eliminar el reporte.', {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar('Ha ocurrido un error al eliminar el reporte.', {
        variant: 'error',
      });
    } finally {
      setLoadingDelete(false);
      handleCloseDelete();
    }
  };

  const frequencies = {
    1: 'Diaria',
    7: 'Semanal',
    30: 'Mensual',
  };

  const ranges = {
    7: 'Una semana',
  };

  return (
    <Card
      className={clsx(classes.rootResults, className)}
      {...rest}
    >
      <Divider />
      <Box
        p={2}
        minHeight={56}
        display="flex"
        alignItems="center"
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              className={classes.queryField}
              onChange={handleQueryChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon
                      fontSize="small"
                      color="action"
                    >
                      <SearchIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
                endAdornment: reportQuery ? (
                  <IconButton size="small" onClick={() => {
                    dispatch(setReportQuery(''));
                    dispatch(setReportPage(0));
                  }}>
                    <XCircleIcon />
                  </IconButton>
                ) : null,
              }}
              placeholder="Buscar por título"
              value={reportQuery || ''}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel variant="outlined" id="order-label">Orden</InputLabel>
              <Select
                labelId="order-label"
                id="order-select"
                value={sort}
                label="Orden"
                onChange={handleSortChange}
                variant="outlined"
              >
                {sortOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    className={classes.orderOption}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} display='flex' justifyContent="end">
            <Button
              color="secondary"
              variant="contained"
              className={classes.action}
              onClick={cleanFilters}
            >
              <SvgIcon
                fontSize="small"
                className={classes.actionIcon}
              >
                <XCircleIcon />
              </SvgIcon>
              Limpiar filtros
            </Button>
          </Grid>
        </Grid>
      </Box>
      <PerfectScrollbar>
        <Box minWidth={700}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align='center'>
                  Título del reporte
                </TableCell>
                <TableCell align='center'>
                  Fecha de creación
                </TableCell>
                <TableCell align='center'>
                  Frecuencia
                </TableCell>
                <TableCell align='center'>
                  Rango
                </TableCell>
                <TableCell align='right'>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentReports.map((report) => (
                <TableRow
                  hover
                  key={report.id}
                >
                  <TableCell align='center'>{report.title}</TableCell>
                  <TableCell align='center'>{report.created_at.split('T')[0]}</TableCell>
                  <TableCell align='center'>{frequencies[report.frequency]}</TableCell>
                  <TableCell align='center'>{report.frequency ? ranges[report.range] : ''}</TableCell>
                  <TableCell align='right'>
                    {account.user.id === report.user_id && (
                      <Tooltip title="Editar reporte">
                        <IconButton
                          component={RouterLink}
                          to={`/administracion/reportes/${report.id}/editar`}
                        >
                          <SvgIcon fontSize="small">
                            <EditIcon />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    )}
                    {account.user.id === report.user_id && (
                      <Tooltip title="Eliminar reporte">
                        <IconButton
                          color="secondary"
                          disabled={loadingDelete}
                          onClick={() => handleOpenDelete(report)}
                        >
                          <SvgIcon fontSize="small">
                            <TrashIcon />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <Box
        className={classes.boxDownTable}
        display='flex'
      >
        <TablePagination
          component="div"
          count={count}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={(reportPage > 0 && currentReports.length < reportLimit) ? 0 : reportPage}
          rowsPerPage={reportLimit}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página"
          style={{ padding: '16px' }}
        />
        <Box
          display='flex'
          flex={1}
          padding="16px"
          justifyContent="end"
          alignItems="center"
        >
          <Button
            variant="contained"
            color="primary"
            type="submit"
            style={{ height: '40px' }}
            component={RouterLink}
            to="/administracion/reportes/crear"
            startIcon={
              <SvgIcon fontSize="medium" className={classes.actionIcon}>
                <PlusSquareIcon />
              </SvgIcon>
            }
          >
            Crear Reporte
          </Button>
        </Box>
      </Box>
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{ paper: classes.createDialogDelete }}
      >
        <DialogTitle disableTypography>
          <Typography className={classes.deleteDialogTypeBold}>
            Eliminar reporte
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.deleteDialogType}>
            ¿Está seguro que desea eliminar el reporte &quot;{reportToDelete?.title}&quot;?
          </Typography>
          <Typography style={{ textAlign: 'center', fontSize: '18px' }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            width: '100%',
            padding: '20px',
          }}
        >
          <Button
            className={classes.createDialogButtonsDownload}
            onClick={handleCloseDelete}
            disabled={loadingDelete}
            variant="contained"
            color="secondary"
          >
            Cancelar
          </Button>
          <Button
            className={classes.createDialogButtonsDownload}
            variant="contained"
            color="primary"
            onClick={handleDelete}
            disabled={loadingDelete}
          >
            {loadingDelete ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

Results.propTypes = {
  className: PropTypes.string,
  reports: PropTypes.array,
  originalReports: PropTypes.array,
  count: PropTypes.number,
  sort: PropTypes.string,
  setSort: PropTypes.func,
  sortOptions: PropTypes.array,
  cleanFilters: PropTypes.func,
  onRefresh: PropTypes.func,
};

Results.defaultProps = {
  products: [],
};

export default Results;
